import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperPlaneTilt, Plus, ArrowsClockwise, Copy } from "@phosphor-icons/react";

const MessagesPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [body, setBody] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [firmUsers, setFirmUsers] = useState([]);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    const r = await api.get("/threads");
    setThreads(r.data.items);
    if (!activeId && r.data.items.length) setActiveId(r.data.items[0].id);
  };

  const loadActive = async (id) => {
    if (!id) return;
    const r = await api.get(`/threads/${id}`);
    setActive(r.data);
  };

  const loadFirm = async () => {
    const r = await api.get("/users/firm");
    setFirmUsers(r.data.items);
  };

  useEffect(() => {
    loadThreads();
    loadFirm();
  }, []);

  useEffect(() => {
    loadActive(activeId);
    const t = setInterval(() => loadActive(activeId), 5000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    try {
      await api.post(`/threads/${activeId}/messages`, { body });
      setBody("");
      loadActive(activeId);
      loadThreads();
    } catch {
      toast.error("Send failed");
    }
  };

  const startDm = async (uid) => {
    const r = await api.post("/threads", { kind: "dm", user_id: uid });
    setShowNew(false);
    await loadThreads();
    setActiveId(r.data.id);
  };

  const copyClientLink = () => {
    if (!active?.client_chat_path) return;
    const url = `${window.location.origin}${active.client_chat_path}`;
    navigator.clipboard.writeText(url);
    toast.success("Client chat link copied.");
  };

  return (
    <div data-testid="messages-page" className="space-y-6">
      <div className="pb-6 border-b border-[#1A2F24]/12 flex items-end justify-between">
        <div>
          <p className="label-eyebrow">Inbox</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Messages</h1>
          <p className="text-[#606C64] mt-2 text-sm max-w-xl">
            Direct messages with your team, plus client conversations linked to engagements.
          </p>
        </div>
        <button
          data-testid="new-thread-btn"
          onClick={() => setShowNew(!showNew)}
          className="btn-primary"
        >
          <Plus size={14} className="inline mr-2" /> New DM
        </button>
      </div>

      {showNew && (
        <div className="card-flat fade-up">
          <p className="label-eyebrow">Start a direct message</p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {firmUsers.length === 0 ? (
              <p className="label-eyebrow text-[#606C64]">No firm members available.</p>
            ) : (
              firmUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => startDm(u.id)}
                  data-testid={`start-dm-${u.id}`}
                  className="text-left p-3 border border-[#1A2F24]/12 hover:border-[#1A2F24] flex items-center gap-3 transition-colors"
                >
                  <div className="w-9 h-9 bg-[#1A2F24] text-[#F7F6F2] flex items-center justify-center font-mono text-xs">
                    {u.avatar || u.name?.[0]}
                  </div>
                  <div>
                    <p className="text-[#112018]">{u.name}</p>
                    <p className="label-eyebrow mt-0.5">{u.title || u.role}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-4 min-h-[60vh]">
        {/* Thread list */}
        <div className="card-flat p-0 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-[#1A2F24]/12 flex items-center justify-between">
            <p className="label-eyebrow">Threads</p>
            <button
              onClick={loadThreads}
              className="p-1 hover:bg-[#efece3]"
              data-testid="refresh-threads"
            >
              <ArrowsClockwise size={14} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 divide-y divide-[#1A2F24]/12">
            {threads.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#606C64]">
                No conversations yet. Start a DM with a team member or open a client chat from an
                engagement.
              </div>
            ) : (
              threads.map((t) => {
                const isActive = t.id === activeId;
                const other = (t.participants || []).find(
                  (p) => p.kind === "user" && p.id !== user?.id,
                );
                const subtitle =
                  t.kind === "client"
                    ? `Client · ${t.client_name}`
                    : other?.title || "Direct message";
                const preview = t.last_message?.body?.slice(0, 60) || "No messages yet";
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveId(t.id)}
                    data-testid={`thread-${t.id}`}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isActive ? "bg-[#1A2F24] text-[#F7F6F2]" : "hover:bg-[#F7F6F2]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p
                        className={`font-medium text-sm ${
                          isActive ? "text-[#F7F6F2]" : "text-[#112018]"
                        }`}
                      >
                        {t.title}
                      </p>
                      {t.kind === "client" && (
                        <span
                          className={`text-[9px] font-mono px-1.5 py-0.5 ${
                            isActive ? "bg-[#A8977B] text-[#1A2F24]" : "bg-[#A8977B]/20 text-[#5a4d34]"
                          }`}
                        >
                          CLIENT
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-xs mt-1 ${
                        isActive ? "text-[#cfd2cc]" : "text-[#606C64]"
                      }`}
                    >
                      {subtitle}
                    </p>
                    <p
                      className={`text-xs mt-1 truncate italic ${
                        isActive ? "text-[#cfd2cc]" : "text-[#606C64]"
                      }`}
                    >
                      {preview}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Active conversation */}
        <div className="card-flat p-0 flex flex-col" data-testid="active-thread">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#606C64] text-sm">
              Select a conversation
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-[#1A2F24]/12 flex items-center justify-between">
                <div>
                  <p className="label-eyebrow">
                    {active.kind === "client" ? "Client chat" : "Direct message"}
                  </p>
                  <p className="font-serif text-2xl text-[#112018]">{active.title}</p>
                </div>
                {active.client_chat_path && (
                  <button
                    onClick={copyClientLink}
                    className="btn-outline"
                    data-testid="copy-client-link"
                  >
                    <Copy size={12} className="inline mr-2" />
                    Copy Client Link
                  </button>
                )}
              </div>
              <div
                ref={scrollRef}
                className="flex-1 p-6 space-y-3 overflow-y-auto"
                style={{ maxHeight: "55vh" }}
              >
                {active.messages.length === 0 ? (
                  <p className="text-center text-sm text-[#606C64] italic py-12">
                    No messages in this thread yet.
                  </p>
                ) : (
                  active.messages.map((m) => {
                    const mine =
                      m.sender_kind === "user" && m.sender_id === user?.id;
                    return (
                      <div
                        key={m.id}
                        className={mine ? "chat-bubble-user" : "chat-bubble-ai"}
                        data-testid={`thread-msg-${m.id}`}
                      >
                        <p className="text-xs opacity-70 mb-1 font-mono">
                          {m.sender_name}
                          {m.sender_kind === "client" && " · client"}
                        </p>
                        <p>{m.body}</p>
                        <p className="text-[10px] opacity-60 mt-1 font-mono">
                          {new Date(m.at).toLocaleString()}
                        </p>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-[#1A2F24]/12 p-3 flex gap-2">
                <input
                  data-testid="thread-input"
                  className="input-clean flex-1"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Write a message…"
                />
                <button
                  onClick={send}
                  className="btn-primary px-4"
                  data-testid="thread-send"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
